import type {
    CoordinateTO,
    FieldErrorTO,
    GeneratedRouteTO,
    HubTO,
    MissionEventTO,
    MissionProgressDetailsDataTO,
    MissionTO,
    PositionTO,
    ProgressDetailsFieldTO,
    ProgressDetailsValueTO,
    StateFieldTO,
    StateValueTO,
} from '@control-center/control-center-client-public-missions-ts';
import { z } from 'zod';

import type {
    MissionEventFE,
    MissionEventTypeFE,
    MissionFE,
    MissionHubFE,
    MissionProgressFE,
    MissionRouteFE,
    MissionStateFE,
} from '../mission';
import type { GeoPositionFE } from '../vehicle';
import {
    fromEnum,
    safeField,
    safeOptional,
    sanitizeWire,
    toDate,
    toGeoPosition,
    toMonitoredField,
} from './restMapping';

/**
 * REST -> domain mapping for missions, served by
 * `@control-center/control-center-client-public-missions-ts` (`MissionsApi`):
 *
 * - `GET /api/v2/missions/active/{vin}`, `GET /api/v2/missions/{missionId}` and every
 *   `POST /api/v2/missions/{missionId}/{start|cancel|archive|reset}` return a `MissionTO`
 *   -> {@link missionFromRestV2}
 * - `GET /api/v2/missions` returns `MissionTO[]` -> {@link missionsFromRestV2}
 * - `GET /api/v2/missions/active/route/{vin}` returns a bare `GeneratedRouteTO`
 *   -> {@link missionRouteFromRestV2}
 *
 * Follows `vehicleFromRestV2.ts`, and is the first consumer of the shared helpers in
 * `restMapping.ts`. Mappers are the only modules allowed to see both a generated client's wire
 * shapes and `src/domain/`.
 *
 * WHY THE WIRE SCHEMAS ARE HAND-WRITTEN HERE, unlike `vehicleFromRestV2.ts`
 * -----------------------------------------------------------------------
 * The missions client ships zod schemas at `dist/schemas`, but they cannot be imported in this
 * workspace. They are generated against zod v4 (`z.iso.datetime()`, `z.uuid()`) while the package
 * declares `"zod": "^3.24.0"`, so npm installs a nested zod 3.25.76 that shadows the workspace's
 * zod 4. Importing the module throws at load time: `Cannot read properties of undefined (reading
 * 'datetime')`. The vehicles client declares `"zod": "^4.3.6"`, gets no nested copy, and that is
 * why `vehicleFromRestV2.ts` can compose its schemas directly.
 *
 * So the field schemas below mirror `dist/schemas.js` by hand, using the workspace zod. The
 * package root (`dist/api`) is zod-free, so the client's TO TYPES are still imported:
 * {@link MissionWireKeysExistOnSpec} and {@link MissionRouteWireKeysExistOnSpec} pair every
 * hand-written schema, nested ones included, with the TO it mirrors and fail the build if a spec
 * change renames or removes a KEY these schemas read. A spec change to a key's TYPE is not caught,
 * by design: that is the runtime degradation described below. `tests/missionClientZodAlignment.test.ts`
 * pins the zod mismatch itself, so the day the client is published with a zod v4 range that test
 * fails and tells us to collapse onto the shipped schemas.
 *
 * DEGRADATION RULES (see the doc comment on `MissionStateFE` in `../mission.ts`)
 * ---------------------------------------------------------------------------
 * Enum-shaped wire fields are typed `z.string()` rather than `z.enum([...])` on purpose. A backend
 * running ahead of this build must degrade ONE field onto 'UNKNOWN', not fail the mission, so the
 * folding happens in the mapper via `fromEnum`. `safeField`'s synthetic PARSING_ERROR is reserved
 * for STRUCTURAL breakage (a non-object envelope, a number where text belongs), which is a real
 * contract break rather than a value this build has not learned yet.
 *
 * `mission_id` and `vin` are the exception: they stay strict and throw. They are the mission's
 * identity, `mission_id` addresses every mission mutation and `vin` ties the mission to its
 * vehicle, so a degraded identity would let the UI fire commands at a mission that does not exist.
 * {@link missionsFromRestV2} keeps that failure scoped to the one entry instead of losing the list.
 *
 * TIMESTAMPS: `Date`, NOT ISO STRINGS - and that is a live contradiction
 * ---------------------------------------------------------------------
 * This mapper emits real `Date` objects, because `../mission.ts` declares every timestamp as
 * `Date | null` and `vehicleFromRestV2`'s `VehicleFE` already does the same. It is deliberately NOT
 * what `docs/poc-domain-mapping-rest-migration.md` prescribes: that doc forbids `Date`, `Map` and
 * `Set` in FE types precisely because RTK Query caches them and `serializableCheck` is enabled in
 * `src/store/setup/index.ts` with only the redux-persist actions ignored.
 *
 * The contradiction is pre-existing and already tolerated: `src/services/vehiclesV2.ts` pushes a
 * Date-bearing `VehicleFE` into the RTK Query cache today. Matching it keeps missions consistent
 * with the shipped vehicle implementation instead of desyncing `mission.ts` from its eventual
 * consumers, but the team owes itself a decision, because:
 *
 * - once `missionV2.ts` / `missionV3.ts` cache the output of this mapper, dev-mode
 *   `serializableCheck` warnings WILL fire on `timestamp`, `actualStartTime`, `actualArrivalTime`,
 *   `plannedArrivalTime`, `events[].createdAt`, `events[].lastRepeatedAt`,
 *   `progressDetails.value.value.eta` and every `MonitoredField.updatedAt`;
 * - the escape hatch already exists: `serializeMonitoredField` in `restMapping.ts` is what
 *   `VehicleSummaryFE` uses to obey the doc. Applying it (plus `.toISOString()` on the plain
 *   timestamps) at the SLICE boundary, not here, is the intended fix if the team decides the
 *   warnings matter - it is a change to `mission.ts` plus the slice, not to the wire parsing.
 */

const MISSION_STATE_PREFIX = 'MISSION_STATE_VALUE_';
const MISSION_EVENT_TYPE_PREFIX = 'MISSION_EVENT_TYPE_';

/**
 * Exported so `services/mappers/missionLiveFieldsFromV2.ts` folds the AppSync enums onto the same
 * domain list this mapper folds the REST wire enums onto, rather than keeping a second copy.
 */
export const MISSION_STATES = [
    'ASSIGNED',
    'READY_TO_LAUNCH',
    'EXECUTING',
    'CANCELED',
    'FAILED',
    'PAUSED',
    'RESET',
    'COMPLETE',
    'LANDED',
    'PAUSING',
    'READY_TO_RESUME',
    'SCHEDULED',
    'UNKNOWN',
] as const satisfies readonly MissionStateFE[];

/** Exported for the same reason as {@link MISSION_STATES}. */
export const MISSION_EVENT_TYPES = [
    'MISSION_ARCHIVED',
    'MISSION_ASSIGNED',
    'MISSION_ASSIGNMENT_FAILED',
    'MISSION_CANCELED',
    'MISSION_COMPLETE',
    'MISSION_EXECUTING',
    'MISSION_LANDED',
    'MISSION_PAUSED_BY_MRM',
    'MISSION_PAUSING_FOR_MRM',
    'MISSION_READY_TO_LAUNCH',
    'MISSION_READY_TO_RESUME',
    'MISSION_RESET',
    'MISSION_RESUMED',
    'MISSION_SCHEDULED',
    'MISSION_VEHICLE_UNASSIGNED_REVERT_SCHEDULED',
    'UNKNOWN',
] as const satisfies readonly MissionEventTypeFE[];

const label = (field: string) => `missionFromRestV2.${field}`;

/** `{ latitude, longitude }`, the route polyline and hub form. */
const WireCoordinateSchema = z.object({
    latitude: z.number().min(-90).max(90),
    longitude: z.number().min(-180).max(180),
});

/**
 * `{ latitude, longitude, altitude_meters, heading_degrees }`, the mission-event form.
 * The spec marks altitude and heading required; they are optional here so an event carrying only
 * a fix still yields a position instead of dropping out of the mission log. A `WireCoordinate` is
 * therefore assignable to this, and both feed the same `toGeoPosition`.
 */
const WirePositionSchema = WireCoordinateSchema.extend({
    altitude_meters: z.number().optional(),
    heading_degrees: z.number().optional(),
});

/** `type` is widened from the wire enum so an unrecognised ERROR_TYPE_* folds to UNKNOWN. */
const WireFieldErrorSchema = z.object({
    type: z.string().optional(),
    updated_at: z.string().optional(),
});

/** `state` is widened from the wire enum so an unmodelled MISSION_STATE_VALUE_* folds to UNKNOWN. */
const WireStateValueSchema = z.object({
    state: z.string().optional(),
    updated_at: z.string().optional(),
});

const WireStateFieldSchema = z.object({
    ttl: z.number().optional(),
    value: WireStateValueSchema.optional(),
    error: WireFieldErrorSchema.optional(),
});

const WireProgressDataSchema = z.object({
    eta: z.string().optional(),
    remaining_distance_in_meters: z.number().optional(),
    traveled_distance_in_meters: z.number().optional(),
});

const WireProgressValueSchema = z.object({
    data: WireProgressDataSchema,
    updated_at: z.string().optional(),
});

const WireProgressDetailsFieldSchema = z.object({
    ttl: z.number().optional(),
    value: WireProgressValueSchema.optional(),
    error: WireFieldErrorSchema.optional(),
});

const WireHubSchema = z.object({
    id: z.string().optional(),
    name: z.string().optional(),
    address: z.string().optional(),
    active: z.boolean().optional(),
    position: WireCoordinateSchema,
});

const WireMissionEventSchema = z.object({
    created_at: z.string().optional(),
    type: z.string().optional(),
    position: WirePositionSchema.optional(),
    repeat_count: z.number().optional(),
    last_repeated_at: z.string().optional(),
});

/**
 * The route object as it appears both nested inside a mission and alone on
 * `GET /api/v2/missions/active/route/{vin}`. Kept un-preprocessed so the enclosing mission's
 * single `sanitizeWire` pass covers it; {@link WireMissionRouteSchema} adds the pass for the
 * standalone endpoint.
 */
const WireMissionRouteObjectSchema = z.object({
    external_route_id: safeOptional(z.string(), label('route.external_route_id')),
    predefined_route_id: safeOptional(z.string(), label('route.predefined_route_id')),
    launch_hub: safeOptional(WireHubSchema, label('route.launch_hub')),
    landing_hub: safeOptional(WireHubSchema, label('route.landing_hub')),
    // Per-entry, so one unusable coordinate shortens the polyline instead of emptying it.
    positions: safeOptional(
        z.array(safeOptional(WireCoordinateSchema, label('route.positions[]'))),
        label('route.positions')
    ),
    total_distance_in_meters: safeOptional(z.number(), label('route.total_distance_in_meters')),
    duration_in_seconds: safeOptional(z.number(), label('route.duration_in_seconds')),
});

const WireMissionObjectSchema = z.object({
    // Identity. Strict on purpose: these must throw, see the module doc comment.
    mission_id: z.string().min(1),
    vin: z.string().min(1),
    fleet_id: safeOptional(z.string(), label('fleet_id')),
    name: safeOptional(z.string(), label('name')),
    description: safeOptional(z.string(), label('description')),
    timestamp: safeOptional(z.string(), label('timestamp')),
    actual_start_time: safeOptional(z.string(), label('actual_start_time')),
    actual_arrival_time: safeOptional(z.string(), label('actual_arrival_time')),
    planned_arrival_time: safeOptional(z.string(), label('planned_arrival_time')),
    // Per-entry, so one unparseable event does not erase the mission's history.
    events: safeOptional(z.array(safeOptional(WireMissionEventSchema, label('events[]'))), label('events')),
    route: safeOptional(WireMissionRouteObjectSchema, label('route')),
    state: safeField(WireStateFieldSchema, label('state')),
    progress_details: safeField(WireProgressDetailsFieldSchema, label('progress_details')),
});

/**
 * Wire schema for a `MissionTO`. Exported so an MSW contract test can assert a mock body against
 * the same validator the mapper uses, the way `WireVehicleSchema` is reused in
 * `mocks/tests/restContract.test.ts`.
 *
 * NOTE FOR CONTRACT TESTS: this is a degrading validator, not a strict one. Every non-identity
 * field is `catchWithLog`-wrapped, so `.parse()` on a body with a corrupt field SUCCEEDS, logs,
 * and returns the field as `undefined`. Only a missing or empty `mission_id` / `vin` throws. A
 * contract test must therefore assert on a `console.error` spy (`expect(spy).not.toHaveBeenCalled()`
 * for a clean body) rather than on parse success, which `mocks/tests/restContract.test.ts` already
 * does for `WireVehicleSchema`.
 */
export const WireMissionSchema = z.preprocess(sanitizeWire, WireMissionObjectSchema);

/**
 * Wire schema for a bare `GeneratedRouteTO`, as returned by the active-route endpoint.
 * Degrades rather than throws, exactly as {@link WireMissionSchema} does - and with no identity
 * field at all, nothing in a route body throws. Same console.error-spy caveat applies.
 */
export const WireMissionRouteSchema = z.preprocess(sanitizeWire, WireMissionRouteObjectSchema);

type WireCoordinate = z.infer<typeof WireCoordinateSchema>;
type WirePosition = z.infer<typeof WirePositionSchema>;
type WireFieldError = z.infer<typeof WireFieldErrorSchema>;
type WireStateField = z.infer<typeof WireStateFieldSchema>;
type WireStateValue = z.infer<typeof WireStateValueSchema>;
type WireProgressDetailsField = z.infer<typeof WireProgressDetailsFieldSchema>;
type WireProgressValue = z.infer<typeof WireProgressValueSchema>;
type WireProgressData = z.infer<typeof WireProgressDataSchema>;
type WireHub = z.infer<typeof WireHubSchema>;
type WireMissionEvent = z.infer<typeof WireMissionEventSchema>;
type WireMissionObject = z.infer<typeof WireMissionObjectSchema>;
type WireRouteObject = z.infer<typeof WireMissionRouteObjectSchema>;

/**
 * `true` only while every key `TWire` reads still exists on the spec type `TSpec`, `never`
 * otherwise - which makes the assignment in `tests/missionFromRestV2.test.ts` fail to compile.
 */
type KeysExistOn<TWire, TSpec> = Exclude<keyof TWire, keyof TSpec> extends never ? true : never;

/**
 * Compile-time link back to the generated client, standing in for the runtime schemas this module
 * cannot import. EVERY hand-written schema is paired with the TO it mirrors, nested shapes
 * included, so renaming or removing a key anywhere in the spec breaks the build instead of
 * silently degrading that field forever. Asserted in `tests/missionFromRestV2.test.ts` rather than
 * here so an unused local does not trip `noUnusedLocals`.
 *
 * Keys only: a key whose TYPE changes on the spec still compiles and degrades at runtime, which is
 * the intended behaviour (see the degradation rules in the module doc comment).
 */
export type MissionWireKeysExistOnSpec = {
    mission: KeysExistOn<WireMissionObject, MissionTO>;
    event: KeysExistOn<WireMissionEvent, MissionEventTO>;
    position: KeysExistOn<WirePosition, PositionTO>;
    state: KeysExistOn<WireStateField, StateFieldTO>;
    stateValue: KeysExistOn<WireStateValue, StateValueTO>;
    progressDetails: KeysExistOn<WireProgressDetailsField, ProgressDetailsFieldTO>;
    progressValue: KeysExistOn<WireProgressValue, ProgressDetailsValueTO>;
    progressData: KeysExistOn<WireProgressData, MissionProgressDetailsDataTO>;
    fieldError: KeysExistOn<WireFieldError, FieldErrorTO>;
};

/**
 * The route half of {@link MissionWireKeysExistOnSpec}. Split out because these shapes are reached
 * both standalone, via `GET /api/v2/missions/active/route/{vin}`, and nested inside a mission.
 */
export type MissionRouteWireKeysExistOnSpec = {
    route: KeysExistOn<WireRouteObject, GeneratedRouteTO>;
    hub: KeysExistOn<WireHub, HubTO>;
    coordinate: KeysExistOn<WireCoordinate, CoordinateTO>;
};

/**
 * A hub without a usable position is dropped: `MissionHubFE.position` is required because every
 * consumer places the hub on the map, and a hub with no coordinates has nothing to render.
 */
const toMissionHub = (raw: WireHub | null | undefined): MissionHubFE | undefined => {
    if (raw == null) {
        return;
    }
    const position = toGeoPosition(raw.position);
    if (position === undefined) {
        return;
    }
    return {
        id: raw.id ?? '',
        name: raw.name ?? '',
        address: raw.address ?? undefined,
        active: raw.active ?? false,
        position,
    };
};

/**
 * An event that could not be parsed at all is dropped rather than fabricated: an unrecognised
 * `type` is kept and labelled 'UNKNOWN' (the log entry still carries meaning), but a structurally
 * broken entry has no type to label, and inventing one would put an event in the mission history
 * that never happened.
 *
 * `created_at` and `type` are both required on `MissionEventTO`, so an entry carrying NEITHER is
 * broken by the spec's own definition and is dropped too - otherwise `{}` or `{ foo: 1 }` would
 * reach the mission log as a typeless, timeless row. One of the two is enough: an entry with a
 * timestamp but a renamed type still belongs in the history as 'UNKNOWN', and vice versa. Dropped
 * silently, like a hub with no position: the entry carries nothing to report, and anything
 * structurally broken was already logged by the per-entry `safeOptional` around this schema.
 */
const toMissionEvent = (raw: WireMissionEvent | null | undefined): MissionEventFE | undefined => {
    if (raw == null || (raw.created_at === undefined && raw.type === undefined)) {
        return;
    }
    const event: MissionEventFE = {
        type: fromEnum<MissionEventTypeFE>(raw.type, MISSION_EVENT_TYPE_PREFIX, MISSION_EVENT_TYPES),
        createdAt: toDate(raw.created_at) ?? null,
        lastRepeatedAt: toDate(raw.last_repeated_at) ?? null,
    };
    const position = toGeoPosition(raw.position);
    if (position !== undefined) {
        event.position = position;
    }
    if (typeof raw.repeat_count === 'number') {
        event.repeatCount = raw.repeat_count;
    }
    return event;
};

const toMissionProgress = (raw: WireProgressData | null | undefined): MissionProgressFE | undefined => {
    if (raw == null) {
        return;
    }
    return {
        eta: toDate(raw.eta) ?? null,
        remainingDistanceInMeters: raw.remaining_distance_in_meters ?? undefined,
        traveledDistanceInMeters: raw.traveled_distance_in_meters ?? undefined,
    };
};

const toMissionRoute = (raw: WireRouteObject): MissionRouteFE => ({
    externalRouteId: raw.external_route_id ?? undefined,
    predefinedRouteId: raw.predefined_route_id ?? undefined,
    launchHub: toMissionHub(raw.launch_hub),
    landingHub: toMissionHub(raw.landing_hub),
    positions: (raw.positions ?? []).reduce<GeoPositionFE[]>((acc, entry) => {
        const position = toGeoPosition(entry);
        if (position !== undefined) {
            acc.push(position);
        }
        return acc;
    }, []),
    totalDistanceInMeters: raw.total_distance_in_meters ?? undefined,
    durationInSeconds: raw.duration_in_seconds ?? undefined,
});

/** Maps one `MissionTO` body onto the domain {@link MissionFE}. Throws only on a missing identity. */
export const missionFromRestV2 = (raw: unknown): MissionFE => {
    const wire = WireMissionSchema.parse(raw);

    const events = (wire.events ?? []).reduce<MissionEventFE[]>((acc, entry) => {
        const event = toMissionEvent(entry);
        if (event !== undefined) {
            acc.push(event);
        }
        return acc;
    }, []);

    return {
        id: wire.mission_id,
        vin: wire.vin,
        fleetId: wire.fleet_id ?? undefined,
        name: wire.name ?? '',
        description: wire.description ?? undefined,
        timestamp: toDate(wire.timestamp) ?? null,
        actualStartTime: toDate(wire.actual_start_time) ?? null,
        actualArrivalTime: toDate(wire.actual_arrival_time) ?? null,
        plannedArrivalTime: toDate(wire.planned_arrival_time) ?? null,
        events,
        route: wire.route == null ? undefined : toMissionRoute(wire.route),
        state: toMonitoredField(
            wire.state,
            value => fromEnum<MissionStateFE>(value.state, MISSION_STATE_PREFIX, MISSION_STATES),
            value => value.updated_at
        ),
        progressDetails: toMonitoredField(
            wire.progress_details,
            value => toMissionProgress(value.data),
            value => value.updated_at
        ),
    };
};

/** Maps a bare `GeneratedRouteTO` body onto the domain {@link MissionRouteFE}. */
export const missionRouteFromRestV2 = (raw: unknown): MissionRouteFE =>
    toMissionRoute(WireMissionRouteSchema.parse(raw));

/**
 * Maps a `GET /api/v2/missions` body. Each entry is mapped on its own, so a mission whose identity
 * is unusable is dropped and logged while every other mission still reaches the UI. Use this
 * instead of `body.map(missionFromRestV2)`, which one bad entry would turn into a throw that
 * empties the whole list.
 */
export const missionsFromRestV2 = (raw: unknown): MissionFE[] => {
    if (!Array.isArray(raw)) {
        console.error(`Zod catch triggered (${label('list')})`, raw);
        return [];
    }
    return raw.reduce<MissionFE[]>((acc, entry) => {
        try {
            acc.push(missionFromRestV2(entry));
        } catch (error) {
            console.error(`Zod catch triggered (${label('list[]')})`, entry, error);
        }
        return acc;
    }, []);
};
